<script setup>

import notification from "@/utils/plugins/NotificationService";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SharedButtonPlus from "@/components/shared/SharedButtonPlus.vue";
import PresenceUserDialog from "@/components/shared/Presence/PresenceUserDialog.vue";
import PresenceDialogChanges from "@/components/shared/Presence/PresenceDialogChanges.vue";
import moment from "moment";
import PresenceService from "@/api/services/newPresencia.service";
import CommonService from "@/api/services/common.service";
import { fetchAllPresenceRecords } from "@/utils/presenceExcelReport";
import { EMPTY_BALANCE, toViewBalance, balanceDayNotes } from "@/utils/presenceBalance";
import { ref, computed, onMounted, onUnmounted, reactive, watch } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { usePresenceStore } from "@/app/stores/presenceStore";
import { useUiStore } from "@/app/stores/uiStore";
import { useRoute } from "vue-router";


import StopSignInModal from "@/components/shared/Presence/StopSignInDialog.vue";

const authStore = useAuthStore();
const presenceStore = usePresenceStore();
const uiStore = useUiStore();
const route = useRoute();
const props = defineProps({
  email: {
    type: String,
  },
  name: {
    type: String,
  }
})

const pickerOptions = ref({
  firstDayOfWeek: 1
})
const employeeName = ref('')
const employeeRouter = computed(() => {
  return props.name;
})
const emailRouter = computed(() => {
  return props.email;
})
// Balances calculados en el back (GET /work_records/balance): misma regla que el informe.
const periodBalanceData = ref(null);
const todayBalanceData = ref(null);
let periodBalanceRange = null;
let periodBalanceRequest = 0;
let todayBalanceRequest = 0;
const BALANCE_REFRESH_MS = 60 * 1000;
const balanceTimer = ref(null);

const daterange = ref([])
const now = ref(moment(new Date()))
const timer = ref(null);
const currentDay = ref('')
const currentSign = ref([])
// Watcher for route id
watch(employeeRouter, (newVal) => {
  if (newVal) {
    employeeName.value = newVal;
  }
})

watch(emailRouter, (newVal) => {
  if (newVal) {
    getPresenceChanges();
    startData();
  }
})

const signings = ref([])
const isBeenClicked = ref(false);
const waitingSignUp = ref(false);

// Presencia Modal
const showModalChangePresence = ref(false);
const selectedSign = ref(null);

const showModalNewPresence = ref(false);
const newDatePresence = ref(null);
const newSigning = ref();
const canChangeNewDay = ref(false);

const disableFutureDates = (date) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return date.getTime() > today.getTime();
}

const isAccessedAdmin = ref(true);
const showModalSeeChanges = ref(false);
const presesenceChanges = reactive([]);
const errorDatePickerNewPresence = ref(false);
const idRouter = ref();

const modalStopSignIn = ref(false);

// eslint-disable-next-line no-unused-vars
const originalWorkRecords = ref([]);
// eslint-disable-next-line no-unused-vars
const workRecordRequests = ref([]);

// eslint-disable-next-line no-unused-vars
const totalRecords = computed(() => {
  return mergeByDay(originalWorkRecords.value, workRecordRequests.value);
})


// eslint-disable-next-line no-unused-vars
const getUserLogged = computed(() => {
  return authStore.getUserLogged;
})
const signButton = computed(() => presenceStore.sign_button);
const stopButton = computed(() => presenceStore.stop_button);
const currentSignStore = computed(() => presenceStore.currentSign);

const periodBalance = computed(() => toViewBalance(periodBalanceData.value?.totals));
const todayBalance = computed(() => toViewBalance(todayBalanceData.value?.totals));
const hasPeriodBalance = computed(() => Boolean(periodBalanceData.value?.days?.length));
const hasTodayBalance = computed(() => Boolean(todayBalanceData.value?.days?.length));
const sumTimeTotal = computed(() => periodBalance.value.worked);
const sumEstimatedTime = computed(() => hasPeriodBalance.value ? periodBalance.value.estimated : undefined);
const diffTime = computed(() => hasPeriodBalance.value ? moment.duration(periodBalance.value.difference) : '');
const sumTimeToday = computed(() => todayBalance.value.worked);
const sumEstimatedTimeToday = computed(() => hasTodayBalance.value ? todayBalance.value.estimated : undefined);
const diffTimeToday = computed(() => hasTodayBalance.value ? moment.duration(todayBalance.value.difference) : '');

// Todas las pausas reales, incluidas las abiertas de hoy, sin alterar el
// descanso computable que se usa para calcular la diferencia.
const getDayPauses = (day) => (day.signs || []).flatMap((sign, index, signs) => {
  if (!sign.pause) return [];
  const resume = signs[index + 1]?.resume;
  const start = moment(day.date + ' ' + sign.pause.time, 'DD/MM/YYYY HHmmss');
  const end = resume
    ? moment(day.date + ' ' + resume.time, 'DD/MM/YYYY HHmmss')
    : moment(day.date, 'DD/MM/YYYY').isSame(now.value, 'day') ? now.value : null;
  return [{
    reason: sign.pause.pauseReason || 'other',
    text: sign.pause.pauseReasonOtherText || null,
    duration: end ? Math.max(0, end.diff(start)) : 0,
  }];
});
const getPauseSummary = (days) => {
  const totals = new Map();
  days.forEach(day => getDayPauses(day).forEach(pause => {
    const total = totals.get(pause.reason) || { reason: pause.reason, duration: 0, count: 0, texts: [] };
    total.duration += pause.duration;
    total.count++;
    if (pause.text && !total.texts.includes(pause.text)) total.texts.push(pause.text);
    totals.set(pause.reason, total);
  }));
  return [...totals.values()];
};
const pauseSummary = computed(() => getPauseSummary(signings.value));
const todayPauseSummary = computed(() => getPauseSummary(todaySignings.value));
// Tiempo efectivo real de trabajo (sin sumar descansos)
const workedTimeToday = computed(() => {
  return sumTimeToday.value || 0;
})
const workedTimeTotal = computed(() => {
  return sumTimeTotal.value || 0;
})
const todaySignings = ref([])

const getReportPresence = computed(() => {
  return presenceStore.getReportPresence;
})
const getDateRangeReport = computed(() => {
  return presenceStore.getDateRangeReport;
})

const shortcuts = [{
  text: 'Esta semana',
  value: () => {
    // Para "Esta semana", usamos ISO Week para considerar la semana de lunes a domingo.
    const start = moment().startOf('isoWeek')
    const end = moment().endOf('isoWeek')
    return [start.toDate(), end.toDate()]
  },
},
{
  text: 'Semana pasada',
  value: () => {
    // Para la semana pasada, retrocedemos 1 semana y tomamos el inicio y fin de esa semana (ISO Week)
    const start = moment().subtract(1, 'weeks').startOf('isoWeek')
    const end = moment().subtract(1, 'weeks').endOf('isoWeek')
    return [start.toDate(), end.toDate()]
  },
},
{
  text: 'Este mes',
  value: () => {
    const start = moment().subtract(0, 'months').startOf('month')
    const end = moment().subtract(0, 'months').endOf('month')
    return [start.toDate(), end.toDate()];
  }
}];

const getTitle = () => {
  if (!isAccessedAdmin.value) return 'Presencia';
  else return 'Presencia ' + employeeName.value;
}

// Esto es lo que hace para el total de la diferencia entre entrada y salida
const calculateTotal = (enter, out) => {
  // console.log(enter,out ? out : '| sin fichaje salida');
  if (!out)
    return moment.duration(
      now.value.diff(moment(enter, "HHmmss"))
    );
  return moment.duration(
    moment(out, "HHmmss").diff(moment(enter, "HHmmss"))
  );
}
const pauseReasonIcon = (reason) => {
  const map = {
    break: 'mug-hot',
    medical_appointment: 'stethoscope',
    child_conciliation: 'child',
    family_conciliation: 'people-roof',
    other: 'circle-question',
  };
  return map[reason] || 'circle-question';
}
const pauseReasonLabel = (reason) => {
  const map = {
    break: 'DESCANSO',
    medical_appointment: 'CITA MÉDICA',
    child_conciliation: 'CONCILIACIÓN HIJOS',
    family_conciliation: 'CONCILIACIÓN FAMILIAR',
    other: 'OTROS',
  };
  return map[reason] || 'OTROS';
}
// Las paradas de tipo "otros" muestran además el motivo indicado por el empleado.
const pauseReasonDescription = (reason, texts = []) => {
  const label = pauseReasonLabel(reason);
  const reasons = (texts || []).filter(Boolean);
  return (reason || 'other') === 'other' && reasons.length ? label + ': ' + reasons.join(' · ') : label;
}
// eslint-disable-next-line no-unused-vars
const getServerTime = async () => {
  const response = await CommonService.getServerTimestamp();
  now.value = moment(response.data.serverTimestamp, "YYYY-MM-DDTHH:mm:ss");
}

const dailyBalanceSource = computed(() => Object.fromEntries((periodBalanceData.value?.days || [])
  .map(day => [moment(day.date, 'YYYY-MM-DD').format('DD/MM/YYYY'), day])));
const dailyBalances = computed(() => Object.fromEntries(
  signings.value.map(day => [day.date, toViewBalance(dailyBalanceSource.value[day.date])])
));
const dailyDifferences = computed(() => Object.fromEntries(
  Object.entries(dailyBalances.value).map(([date, balance]) => [date, moment.duration(balance.difference)])
));

const getDayBalanceHint = (day) => {
  const balance = dailyBalances.value[day.date] || EMPTY_BALANCE;
  return ['Jornada de convenio: ' + secondsToTime(balance.estimated), ...balanceDayNotes(dailyBalanceSource.value[day.date], secondsToTime)]
    .join(' · ');
}

const balanceParams = (from, to) => {
  const params = { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') };
  if (route.params?.id) params.employee = route.params.id;
  return params;
}
const fetchBalance = (params) => route.params?.id
  ? PresenceService.getWorkRecordsBalanceAdmin(params)
  : PresenceService.getWorkRecordsBalance(params);

const loadPeriodBalance = async (from, to) => {
  const request = ++periodBalanceRequest;
  periodBalanceRange = { from: from.clone(), to: to.clone() };
  const response = await fetchBalance(balanceParams(from, to));
  if (request === periodBalanceRequest) {
    periodBalanceData.value = response?.data || null;
  }
}
const loadTodayBalance = async () => {
  const request = ++todayBalanceRequest;
  const today = moment(now.value);
  const response = await fetchBalance(balanceParams(today, today));
  if (request === todayBalanceRequest) {
    todayBalanceData.value = response?.data || null;
  }
}
// El día en curso con jornada abierta se recalcula en el back cada minuto.
const refreshLiveBalances = () => {
  if (!todayBalanceData.value?.days?.some(day => day.isLive)) return;
  loadTodayBalance();
  if (periodBalanceRange && moment(now.value).isBetween(periodBalanceRange.from, periodBalanceRange.to, 'day', '[]')) {
    loadPeriodBalance(periodBalanceRange.from, periodBalanceRange.to);
  }
}

onUnmounted(() => {
  clearInterval(timer.value);
  clearInterval(balanceTimer.value);
})

onMounted(async () => {
  await getServerTime();
  timer.value = setInterval(() => {
    now.value = now.value.clone().add(1, 'seconds');
  }, 1000);
  balanceTimer.value = setInterval(refreshLiveBalances, BALANCE_REFRESH_MS);
  if (route.params && route.params.id) {
    idRouter.value = route.params.id;
    isAccessedAdmin.value = true;
    daterange.value = [];
    for (let i = 0; i < getDateRangeReport.value.length; i++) {
      const element = getDateRangeReport.value[i];
      daterange.value.push(moment(new Date(element)).format('YYYY-MM-DD'));
    }
  } else {
    isAccessedAdmin.value = false;
    presenceStore.resetReportPresence();
    daterange.value = [];
    employeeName.value = null;
  }
  getPresenceChanges();
  startData();
})

const startData = () => {
  // New WorkRecords
  getWorkRecords();
  getCurrentWorkRecords();
  getWorkRecordStatus();
}

const getNotification = () => {
  let mssg;
  if (signButton.value) {
    mssg = 'Presencia registrada con éxito';
  } else {
    mssg = 'Presencia finalizada con éxito';
  }
  notification.addNotification({
    type: 'success',
    message: mssg,
    duration: 6000
  })
}

const addSignUp = async () => {
  isBeenClicked.value = true;
  waitingSignUp.value = true;
  //closeSignModal();
  //const user = getUserLogged;
  presenceStore.setStatusSignButton();
  await presenceStore.addSigning();
  await getServerTime();
  getWorkRecords();
  getCurrentWorkRecords();
}
const onchangeDateRange = () => {
  if (daterange.value?.length !== 2) getWorkRecords(false);
  else getWorkRecords(true);
}

// eslint-disable-next-line no-unused-vars
const durationFormat = (d) => {
  if (!d) return "h:m";

  return moment(d.asMilliseconds()).format("HH:mm");
}

const secondsToTime = (value) => {
  const milliseconds = Number(value);
  if (!Number.isFinite(milliseconds)) return "00h:00m";
  const totalMinutes = Math.floor(Math.abs(milliseconds) / 60000);
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const minutes = String(totalMinutes % 60).padStart(2, "0");
  return (milliseconds < 0 && totalMinutes > 0 ? "-" : "") + hours + "h:" + minutes + "m";
}
const formatPresenceDate = (value) => {
  const date = moment(value, "DD/MM/YYYY", true);
  return date.isValid() && date.day() === 5 ? value + " (V)" : value;
}

// eslint-disable-next-line no-unused-vars
const formatDate = (value) => {
  if (value) {
    return moment(value).format("DD/MM/YYYY");
  }
  return "";
}
// eslint-disable-next-line no-unused-vars
const formatDateTime = (value) => {
  if (value) {
    return moment(value).format("DD/MM/YYYY HH:mm");
  }
  return "";
}
// eslint-disable-next-line no-unused-vars
const formatTime = (value) => {
  if (value) {
    return moment(value).format("HH:mm") + "h";
  }
  return "";
}
// eslint-disable-next-line no-unused-vars
const formatTimeStyle = (value) => {
  if (value) {
    let hours =
      value.hours().toString().length === 1
        ? "0" + value.hours().toString()
        : value.hours();
    let minutes =
      value.minutes().toString().length === 1
        ? "0" + value.minutes().toString()
        : value.minutes();

    return hours + "h:" + minutes + "m";
  }
  return "";
}
// eslint-disable-next-line no-unused-vars
const formatTimeStyleTotals = (value) => {
  if (value) {
    let hours =
      value.hours().toString().length === 1
        ? "0" + value.hours().toString()
        : value.hours();
    let minutes =
      value.minutes().toString().length === 1
        ? "0" + value.minutes().toString()
        : value.minutes();

    return hours + ":" + minutes + "h";
  }
  return "";
}
const isPositiveDuration = (value) => {
  return Boolean(value && typeof value.asMilliseconds === "function" && Math.trunc(value.asMilliseconds() / 60000) >= 0);
}
const isNegativeDuration = (value) => {
  return Boolean(value && typeof value.asMilliseconds === "function" && Math.trunc(value.asMilliseconds() / 60000) < 0);
}
// eslint-disable-next-line no-unused-vars
const formatTimeWithOutStyle = (value) => {
  if (value) {
    if (value.time) {
      return moment(value.time, "HHmmss").format("HH:mm");
    }
  }
  return "";
}
// eslint-disable-next-line no-unused-vars
const formatTimeWithH = (value) => {
  if (value) {
    return moment(value, "HHmmss").format("HH:mm") + "h";
  }
  return "";
}
// eslint-disable-next-line no-unused-vars
const welcome = (value) => {
  let currentHour = moment(value).format("HH");
  let textWelcome = "";
  if (currentHour >= 6 && currentHour < 12) {
    console.log("Buenos dias", currentHour);
    textWelcome = "Buenos días";
  } else if (currentHour >= 12 && currentHour < 20) {
    console.log("Buenos tardes", currentHour);
    textWelcome = "Buenas tardes";
  } else if (currentHour >= 20 || currentHour < 6) {
    console.log("Buenas noches", currentHour);
    textWelcome = "Buenas noches";
  }
  return textWelcome;
}

let workRecordsRequest = 0;
const getWorkRecords = async (isfilter = false) => {
  const request = ++workRecordsRequest;
  //const user = getUserLogged;
  let from_date = "";
  let to_date = "";
  let email = props.email ? props.email : '';
  const day_week = moment(now.value).day();

  if (!isfilter) {
    if (day_week <= 1) {
      from_date = moment().weekday(-7);
      to_date = moment().weekday(0);
    } else {
      from_date = moment().weekday(0);
      // to_date = moment().weekday(5);
      to_date = moment().weekday(day_week - 1);
    }
  } else {
    from_date = moment(daterange.value[0]);
    to_date = moment(daterange.value[1]);
  }
  // console.log("FECHAS: ", from_date.format("YYYY-MM-DD"), to_date.format("YYYY-MM-DD"));
  signings.value = [];
  currentSign.value = [];
  if (route.params && route.params.id) {
    const report = getReportPresence.value.filter(v => v.id == route.params.id)[0];
    if (daterange.value.length > 0) {
      from_date = moment(daterange.value[0])
      to_date = moment(daterange.value[1]);
    }
    if (report && report.name) {
      employeeName.value = report.name;
    } else {
      if (employeeRouter.value) {
        employeeName.value = employeeRouter.value;
      }
    }
  } else employeeName.value = null;
  currentDay.value = from_date.format("DD/MM/YYYY") + ' - ' + to_date.format("DD/MM/YYYY");
  from_date.hour(0).minute(0).second(0);
  to_date.hour(23).minute(59).second(59);
  const params = {
    "recordedAt[after]": from_date.format("YYYY-MM-DDTHH:mm:ss"),
    "recordedAt[before]": to_date.format("YYYY-MM-DDTHH:mm:ss"),
    itemsPerPage: 50,
    "order[recordedAt]": "ASC"
  }
  const isEmployeeDetail = Boolean(route.params?.id);
  if (isEmployeeDetail && !email) return;
  if (isEmployeeDetail) {
    params["employee.email"] = email;
  }
  loadPeriodBalance(from_date, to_date);
  uiStore.startLoading();
  try {
    const records = await fetchAllPresenceRecords(
      pageParams => isEmployeeDetail
        ? PresenceService.getWorkRecordsUserAdmin(pageParams)
        : PresenceService.getWorkRecordsUser(pageParams),
      params,
    );
    if (request !== workRecordsRequest) return;
    doPromiseWorkRecords({ data: { 'hydra:member': records } }, isfilter);
    if (!isEmployeeDetail && getUserLogged.value) {
      employeeName.value = getUserLogged.value.name + " " + getUserLogged.value.lastName;
    }
  } catch {
    if (request === workRecordsRequest) {
      notification.addNotification({ message: 'No se han podido obtener todos los fichajes de presencia', type: 'error' });
    }
  } finally {
    uiStore.stopLoading();
  }
}

const doPromiseWorkRecords = (res, isfilter) => {
  if (res.data && res.data["hydra:member"].length > 0) {
    const grouped = groupByDay(res.data["hydra:member"]);
    originalWorkRecords.value = Object.entries(grouped).map(([day, requests]) => ({ day, requests }));
    signings.value = [];
    Object.entries(grouped).forEach(([day, element]) => {
      let object = {};
      let flag = true;
      let signs = [];
      let tTime = 0;
      element.signings.forEach((v) => {
        if (flag) {
          object.remote = v.remote ? v.remote : true;
          object[v.type] = {
            "time": moment(v.recordedAt).format("HHmmss"),
            "id": v["@id"],
            "type": v.type,
            ...(v.pauseReason && { "pauseReason": v.pauseReason }),
            ...(v.pauseReasonOtherText && { "pauseReasonOtherText": v.pauseReasonOtherText })
          };
          signs.push(object);
        } else {
          object.remote = v.remote ? v.remote : true;
          object[v.type] = {
            "time": moment(v.recordedAt).format("HHmmss"),
            "id": v["@id"],
            "type": v.type,
            ...(v.pauseReason && { "pauseReason": v.pauseReason }),
            ...(v.pauseReasonOtherText && { "pauseReasonOtherText": v.pauseReasonOtherText })
          };
          const inRecord = object.entry ?? object.resume;
          const inn = moment(inRecord.time, "HHmmss");
          const out = moment(object[v.type].time, "HHmmss");

          object.total = moment.duration(out.diff(inn));
          if (tTime === 0)
            tTime = object.total.asMilliseconds();
          else
            tTime += object.total.asMilliseconds();

          signs[signs.length - 1] = object;

          object = {};
        }
        flag = !flag;
      });

      let tBreak = 0;
      for (let i = 0; i < signs.length - 1; i++) {
        if (signs[i].pause && signs[i + 1] && signs[i + 1].resume) {
          const pauseTime = moment(signs[i + 1].resume.time, "HHmmss")
            .diff(moment(signs[i].pause.time, "HHmmss"));
          tBreak += pauseTime;
        }
      }

      const firstInRecord = signs[0].entry ?? signs[0].resume;
      signings.value.push({
        date: moment(day, "YYYYMMDD").format("DD/MM/YYYY"),
        signs,
        total: tTime === 0 ? moment.duration(now.value.diff(moment(firstInRecord.time, "HHmmss"))).asMilliseconds() : tTime,
        breakTotal: tBreak,
      });
      tTime = 0;
    });
    signings.value.sort((a, b) => {
      return moment(a.date, "DD/MM/YYYY").diff(moment(b.date, "DD/MM/YYYY"));
    })
  } else {
    presenceStore.setSignButton(true);
  }
  if (isBeenClicked.value) {
    getNotification();
    isBeenClicked.value = false;
    setTimeout(() => {
      waitingSignUp.value = false;
    }, 4000)
  }
  if (!isfilter) {
    todaySignings.value = [];
    signings.value.forEach((val) => {
      if (val.date === moment(now.value).format('DD/MM/YYYY')) {
        todaySignings.value.push(val)
      }
    })
  }
}

const groupByDay = (data) => {
  return data.reduce((acc, item) => {
    // Obtener solo la fecha en formato YYYY-MM-DD
    const day = moment(item.recordedAt).format("YYYY-MM-DD");

    // Si el día no existe en el acumulador, lo inicializamos con un array vacío
    if (!acc[day]) {
      acc[day] = { signings: [] };
    }
    // Agregamos el item al array del día correspondiente
    acc[day].signings.push(item);
    return acc;
  }, {});
}

const getCurrentWorkRecords = () => {
  let from_date = moment(now);
  from_date.hour(0).minute(0).second(0);
  let to_date = moment(now);
  to_date.hour(23).minute(59).second(59);
  const email = props.email ? props.email : '';
  let params = {
    "recordedAt[after]": from_date.format("YYYY-MM-DDTHH:mm:ss"),
    "recordedAt[before]": to_date.format("YYYY-MM-DDTHH:mm:ss"),
    itemsPerPage: 50,
    "order[recordedAt]": "ASC"
  }
  const presenceRequest = isAccessedAdmin.value && email
    ? PresenceService.getWorkRecordsUserAdmin({ ...params, "employee.email": email })
    : PresenceService.getWorkRecordsUser(params);
  loadTodayBalance();

  presenceRequest.then(response => {
    const newArray = [];
    if (!isAccessedAdmin.value) {
      if (response.data["hydra:member"].length % 2 === 0) {
        presenceStore.setSignButton(true);
      } else {
        presenceStore.setSignButton(false);
      }
    }

    for (let i = 0; i < response.data["hydra:member"].length; i++) {
      const element = response.data["hydra:member"][i];
      let newT = {};
      newT.time = moment(element.recordedAt).format('HHmmss');
      newT.type = element.type;
      if (element.pauseReason) newT.pauseReason = element.pauseReason;
      if (element.pauseReasonOtherText) newT.pauseReasonOtherText = element.pauseReasonOtherText;
      newArray.push(newT);
    }
    presenceStore.currentPresence(newArray);
    if (!isAccessedAdmin.value) {
      getWorkRecordStatus();
    }
  })
}

const formatRequestedWorkRecords = (requestedWorkRecords) => {
  const temporal = [];

  for (let i = 0; i < requestedWorkRecords.length; i++) {
    const element = requestedWorkRecords[i];
    const temp = {};
    temp.time = moment(element.requestedRecordedAt).format('HHmmss');
    temp.class = element.requestType;
    temp.type = element.type;
    temp.id = element.id;
    temp.existingRequestId = element.id;
    temp.originalWorkRecord = element.originalWorkRecord;

    if (element.originalWorkRecord) {
      temp.originalId = element.originalWorkRecord;
    }

    if (element.pauseReason) temp.pauseReason = element.pauseReason;
    if (element.pauseReasonOtherText) temp.pauseReasonOtherText = element.pauseReasonOtherText;

    temporal.push(temp);
  }

  return temporal;
}

// Funciones Dialog
const openDialog = async (sign) => {
  selectedSign.value = sign;
  const params = {
    'requestedRecordedAt[after]': moment(sign.date, 'DD-MM-YYYY').hour(0).minute(0).seconds(0).format('YYYY-MM-DDTHH:mm:ss'),
    'requestedRecordedAt[before]': moment(sign.date, 'DD-MM-YYYY').hour(23).minute(59).seconds(59).format('YYYY-MM-DDTHH:mm:ss'),
    status: 'pending',
  }
  await PresenceService.getWorkRecordsRequestUser(params)
    .then((res) => {
      if (res && res.data['hydra:member'].length > 0 && res.status == 200) {
        selectedSign.value.requestedWorkRecords = formatRequestedWorkRecords(res.data['hydra:member']);
      } else {
        selectedSign.value = sign;
      }
    }).catch((err) => {
      console.log(err);
      selectedSign.value = sign;
    })
  showModalChangePresence.value = true;
}

const closeModal = () => {
  showModalChangePresence.value = false;
  selectedSign.value = null;
}

const openNewPresence = () => {
  showModalNewPresence.value = true;
}

const closeNewModal = () => {
  showModalNewPresence.value = false;
  newDatePresence.value = null;
  newSigning.value = {}
  canChangeNewDay.value = false;
}

const setNewDay = () => {
  if (!newDatePresence.value) {
    errorDatePickerNewPresence.value = true;
    return;
  }
  errorDatePickerNewPresence.value = false;
  searchWorkRecordDay(newDatePresence.value);
}
const searchWorkRecordDay = async (day) => {
  if (!day) {
    return [];
  }
  canChangeNewDay.value = false;
  newSigning.value = {};
  let hasOriginalWorkRecords = false;
  const from_date = moment(new Date(day));
  const to_date = moment(new Date(day));
  from_date.hour(0).minute(0).second(0);
  to_date.hour(23).minute(59).second(59);

  try {
    const res = await PresenceService.getWorkRecordsUser({
      "recordedAt[after]": from_date.format("YYYY-MM-DDTHH:mm:ss"),
      "recordedAt[before]": to_date.format("YYYY-MM-DDTHH:mm:ss"),
      itemsPerPage: 50,
    });

    if (res.data && res.data["hydra:member"].length > 0) {
      hasOriginalWorkRecords = true;
      const grouped = groupByDay(res.data["hydra:member"]);
      Object.entries(grouped).forEach(([day, element]) => {
        let object = {};
        let flag = true;
        let signs = [];
        let tTime = 0;
        element.signings.forEach((v) => {
          if (flag) {
            object.remote = v.remote ? v.remote : true;
            object[v.type] = {
              "time": moment(v.recordedAt).format("HHmmss"),
              "id": v["@id"],
              "type": v.type,
              ...(v.pauseReason && { "pauseReason": v.pauseReason }),
              ...(v.pauseReasonOtherText && { "pauseReasonOtherText": v.pauseReasonOtherText })
            };
            signs.push(object);
          } else {
            object.remote = v.remote ? v.remote : true;
            object[v.type] = {
              "time": moment(v.recordedAt).format("HHmmss"),
              "id": v["@id"],
              "type": v.type,
              ...(v.pauseReason && { "pauseReason": v.pauseReason }),
              ...(v.pauseReasonOtherText && { "pauseReasonOtherText": v.pauseReasonOtherText })
            };
            const inRecord = object.entry ?? object.resume;
            const inn = moment(inRecord.time, "HHmmss");
            const out = moment(object[v.type].time, "HHmmss");

            object.total = moment.duration(out.diff(inn));
            if (tTime === 0)
              tTime = object.total.asMilliseconds();
            else
              tTime += object.total.asMilliseconds();

            signs[signs.length - 1] = object;

            object = {};
          }
          flag = !flag;
        });

        let tBreak = 0;
          for (let i = 0; i < signs.length - 1; i++) {
          if (signs[i].pause && signs[i + 1] && signs[i + 1].resume) {
            const pauseTime = moment(signs[i + 1].resume.time, "HHmmss")
              .diff(moment(signs[i].pause.time, "HHmmss"));
            tBreak += pauseTime;
          }
        }

        const firstInRecord = signs[0].entry ?? signs[0].resume;
        newSigning.value = {
          date: moment(day, "YYYYMMDD").format("DD/MM/YYYY"),
          signs,
          total: tTime === 0 ? moment.duration(now.value.diff(moment(firstInRecord.time, "HHmmss"))).asMilliseconds() : tTime,
          breakTotal: tBreak,
        };
        tTime = 0;
      });
    } else {
      newSigning.value = {};

      newSigning.value = {
        date: moment(day, "YYYYMMDD").format("DD/MM/YYYY"),
        signs: [],
        newDay: true,
      };
    }

    const requestParams = {
      'requestedRecordedAt[after]': from_date.format("YYYY-MM-DDTHH:mm:ss"),
      'requestedRecordedAt[before]': to_date.format("YYYY-MM-DDTHH:mm:ss"),
      status: 'pending',
    };

    const requestRes = await PresenceService.getWorkRecordsRequestUser(requestParams);
    if (requestRes && requestRes.status == 200 && requestRes.data['hydra:member'].length > 0) {
      if (!hasOriginalWorkRecords) {
        newSigning.value = {
          date: moment(day, "YYYYMMDD").format("DD/MM/YYYY"),
          signs: [],
        };
      }
      newSigning.value.requestedWorkRecords = formatRequestedWorkRecords(requestRes.data['hydra:member']);
    }

    if (newSigning.value.signs) {
      canChangeNewDay.value = true;
    }
  } catch (err) {
    console.log(err);
    newSigning.value = {};
  }

}

// Funciones Admin
// Modal Revisar Cambios
const openSeeChanges = async () => {
  await getPresenceChanges();
  showModalSeeChanges.value = true;
}

const closeSeeChanges = () => {
  showModalSeeChanges.value = false;
}

const getPresenceChanges = () => {
  const params = {
    "status": "pending",
  }
  uiStore.startLoading()
  PresenceService.getWorkRecordsRequestUser(params)
    .then((res) => {
      const result = groupAndSortWorkRequest(res.data["hydra:member"]);
      for (let i = 0; i < result.length; i++) {
        result[i].isAccepted = true;
      }
      uiStore.stopLoading()
      presesenceChanges.length = 0;
      presesenceChanges.push(...result)
      workRecordRequests.value = presesenceChanges;

      // TODO: Añadir aqui modified workrecords
    }).catch(() => {
      uiStore.stopLoading()
    })
}

const finishDialogChanges = () => {
  notification.addNotification({
    type: 'success',
    message: 'Cambios terminados',
    duration: 6000
  })
  closeSeeChanges();
  getWorkRecords();
  getCurrentWorkRecords();
  getPresenceChanges();
}

const groupAndSortWorkRequest = (data) => {
  const grouped = data.reduce((acc, item) => {
    const dayKey = moment(item.requestedRecordedAt).format('DD-MM-YYYY');
    if (!acc[dayKey]) acc[dayKey] = [];
    acc[dayKey].push(item);
    return acc;
  }, {});

  let result = Object.entries(grouped).map(([dayKey, requests]) => ({
    day: moment(dayKey, 'DD-MM-YYYY').format('DD-MM-YYYY'),
    _dayKey: dayKey,       // guardamos el key para ordenar
    requests
  }));
  result.sort((a, b) => a._dayKey.localeCompare(b._dayKey));
  // eslint-disable-next-line
  result = result.map(({ _dayKey, ...rest }) => rest);

  const typeOrder = { entry: 0, exit: 1 };
  result.forEach(group => {
    group.requests.sort((a, b) => {
      const ta = moment(a.requestedRecordedAt).valueOf();
      const tb = moment(b.requestedRecordedAt).valueOf();
      if (ta !== tb) return ta - tb;
      return typeOrder[a.type] - typeOrder[b.type];
    });
  });

  return result;
}

const mergeByDay = (arr1, arr2) => {
  const resultMap = new Map();

  // Procesa el primer array (originalWorkRecords)
  arr1.forEach(({ day, requests }) => {
    requests = requests.signings

    day = moment(day, 'YYYY-MM-DD').format('DD-MM-YYYY')
    resultMap.set(day, {
      day: normalizeDay(day),
      isAccepted: true,
      requests: {
        originalWorkRecords: requests,
        workRecordRequests: []
      }
    });
  });
  // Segundo: workRecordsRequests
  arr2.forEach(({ day, requests }) => {
    const entry = resultMap.get(day);

    if (requests && requests.length) {
      if (entry) {
        // Si ya existe, solo añadimos requests
        entry.requests.workRecordRequests = requests;
      } else {
        // Si no existe, lo creamos
        resultMap.set(day, {
          day,
          isAccepted: true,
          requests: {
            originalWorkRecords: [],
            workRecordRequests: requests
          }
        });
      }
    }
    // Si requests está vacío o es falsy, no hacemos nada
  });

  // Convertimos a array y filtramos entradas sin requests válidos
  return Array.from(resultMap.values()).filter(item =>
    Array.isArray(item.requests.workRecordRequests) &&
    item.requests.workRecordRequests.length > 0
  ).sort((a, b) => moment(a.day, 'DD-MM-YYYY').toDate() - moment(b.day, 'DD-MM-YYYY').toDate());
}

const normalizeDay = (day) => {
  const sep = day.includes('/') ? '/' : '-';
  const parts = day.split(sep);

  let d, m, y;

  if (parts[0].length === 4) {
    // Formato ISO: YYYY-MM-DD
    y = parts[0];
    m = parts[1];
    d = parts[2];
  } else {
    const first = parseInt(parts[0], 10);
    const second = parseInt(parts[1], 10);

    if (first > 12 && second <= 12) {
      d = parts[0];
      m = parts[1];
    } else {
      d = parts[0];
      m = parts[1];
    }
    y = parts[2];
  }

  d = d.padStart(2, '0');
  m = m.padStart(2, '0');

  return `${d}-${m}-${y}`;
}

const stopSignIn = async () => {
  if (waitingSignUp.value || signButton.value) return;
  waitingSignUp.value = true;
  if (stopButton.value) {
    openDialogModalStopSignIn();
    await getServerTime();
  } else {
    await presenceStore.addStop();
    await getServerTime();
    getWorkRecords();
    getCurrentWorkRecords();
    waitingSignUp.value = false;
    notification.addNotification({
      type: 'success',
      message: 'Presencia reanudada',
      duration: 6000
    })
  }
}

const confirmDialogModalStopSignIn = async (selectedReason) => {
  if (selectedReason) {
    // La parada ya se ha guardado en StopSignInDialog.
    await getServerTime();
    getWorkRecords();
    getCurrentWorkRecords();
    // stopWorker();


  }
}

const openDialogModalStopSignIn = () => {
  modalStopSignIn.value = true;
}

const getWorkRecordStatus = async () => {
  await PresenceService.getWorkRecordsStatus({})
    .then((response) => {
      if (response && (response.status === 200 || response.status === 201)) {
        const status = response.data
        presenceStore.setStatusButtons(status)
      }
    })
}

</script>

<template>
  <div>
    <HeaderTitle :title="getTitle()" />
    <div class="content">
      <!-- Cabezera boton -->
      <div class="header">
        <!-- Cabezera boton -->
        <el-row class="d-flex align-items-center justify-content-start flex-wrap">
          <div class="d-flex align-items-center">
            <svg id="chronometer" version="1.1" xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
              style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
              <g>
                <path d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                            c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                            l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                            C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                            c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                            c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                            s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                            c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                            M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                            S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                            c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                            C124.383,80.339,121.025,76.981,116.883,76.981z" />
              </g>
            </svg>
            <div class="text-title">
              <b>Presencia</b>
            </div>
          </div>
          <div class="d-flex flex-wrap justify-content-starts ml-auto mt-3">
            <button :class="signButton ? 'btn_yellow' : 'btn_red'" class="btn btn-block m-2 btn-action"
              @click="addSignUp()" :disabled="waitingSignUp || !stopButton" v-if="!isAccessedAdmin">
              {{ signButton ? "EMPEZAR" : "TERMINAR" }}
            </button>
            <button :class="stopButton ? 'btn_orange' : 'btn_lime'" class="btn btn-block btn-action m-2"
              v-if="!isAccessedAdmin" :disabled="waitingSignUp || signButton" @click="stopSignIn()">
              {{
                stopButton ? 'PARAR' :
                  'REANUDAR'
              }}
            </button>
            <button class="btn btn-block m-2 btn-action btn_yellow" @click="openSeeChanges()">
              {{ isAccessedAdmin ? 'REVISAR CAMBIOS' : 'CAMBIOS PENDIENTES' }}
            </button>
            <slot name="header-actions" />
          </div>
        </el-row>
        <!-- Entradas hoy -->
        <div class="presence-segments">
          <template v-for="(seg, index) in currentSignStore" :key="index">
            <!-- Segmento de trabajo -->
            <el-row v-if="seg.segmentType === 'work'">
              <div class="card card-notice w-100 pt-2 px-2">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="col d-flex align-items-center">
                    <h1>
                      <font-awesome-icon class="iconNav"
                        :icon="seg.inType === 'resume' ? 'arrows-rotate' : 'sign-in-alt'" />
                    </h1>
                    <h6 class="mx-3">
                      <b>{{ seg.inType === 'entry' ? 'ENTRADA' : 'REANUDACIÓN' }}</b>
                    </h6>
                    <h1>{{ formatTimeWithH(seg.in) }}</h1>
                  </div>
                  <div class="col d-flex align-items-center">
                    <h1>
                      <font-awesome-icon class="iconNav"
                        :icon="seg.outType === 'pause' ? pauseReasonIcon(seg.outReason) : 'door-open'" />
                    </h1>
                    <h6 class="mx-3">
                      <b>{{ seg.outType === 'exit' ? 'SALIDA' : 'PARADA' }}</b>
                    </h6>
                    <h1>{{ formatTimeWithH(seg.out) }}</h1>
                  </div>
                  <div class="col d-flex align-items-center">
                    <svg id="chronometer1" version="1.1" xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                      style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                      <g>
                        <path
                          d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                                c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                                l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                                C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                                c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                                c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                                s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                                c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                                M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                                S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                                c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                        <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                                C124.383,80.339,121.025,76.981,116.883,76.981z" />
                      </g>
                    </svg>
                    <h6 class="mx-3">
                      <b>TOTAL</b>
                    </h6>
                    <h1>{{ formatTimeStyle(calculateTotal(seg.in, seg.out)) }}</h1>
                  </div>
                </div>
              </div>
            </el-row>
            <!-- Segmento de pausa -->
            <el-row v-else-if="seg.segmentType === 'pause'" class="presence-header-pause-row">
              <div class="card card-notice w-100 pt-2 px-2 presence-header-pause-card">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="col d-flex align-items-center">
                    <h1>
                      <el-tooltip :content="pauseReasonDescription(seg.reason, [seg.otherText])" placement="top">
                        <font-awesome-icon class="iconNav" :icon="pauseReasonIcon(seg.reason)"
                          :aria-label="pauseReasonDescription(seg.reason, [seg.otherText])" />
                      </el-tooltip>
                    </h1>
                    <h6 class="mx-3">
                      <b>{{ pauseReasonLabel(seg.reason) }}</b>
                    </h6>
                    <h1>{{ formatTimeWithH(seg.start) }}</h1>
                  </div>
                  <div class="col d-flex align-items-center">
                    <h1>
                      <font-awesome-icon class="iconNav" icon="arrows-rotate" />
                    </h1>
                    <h6 class="mx-3"><b>REANUDACIÓN</b></h6>
                    <h1>{{ seg.end ? formatTimeWithH(seg.end) : '' }}</h1>
                  </div>
                  <div class="col d-flex align-items-center">
                    <svg id="chronometer1" version="1.1" xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                      style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                      <g>
                        <path
                          d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                                c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                                l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                                C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                                c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                                c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                                s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                                c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                                M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                                S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                                c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                        <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                                C124.383,80.339,121.025,76.981,116.883,76.981z" />
                      </g>
                    </svg>
                    <h6 class="mx-3"><b>TOTAL</b></h6>
                    <h1>{{ formatTimeStyle(calculateTotal(seg.start, seg.end)) }}</h1>
                  </div>
                </div>
              </div>
            </el-row>
          </template>
        </div>
      </div>
      <!-- Cabezera movil -->
      <div class="header-mobile">
        <div class="card-notice">
          <div class="d-flex align-items-center row">
            <div class="d-flex el-col-xs-12">
              <svg id="chronometer" version="1.1" xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                <g>
                  <path d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                            c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                            l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                            C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                            c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                            c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                            s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                            c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                            M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                            S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                            c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                  <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                            C124.383,80.339,121.025,76.981,116.883,76.981z" />
                </g>
              </svg>
              <div class="text-title-header">
                <b>Presencia</b>
              </div>
            </div>
            <div class="d-flex el-col-xs-12 justify-content-end align-items-center">
              <div class="d-flex align-items-center">
                <span class="mx-2 text-total-header">
                  Total:
                </span>
                <span class="totalTime-header">{{ secondsToTime(workedTimeToday) }}</span>
              </div>
            </div>
            <template v-for="(seg, index) in currentSignStore" :key="index">
              <el-row v-if="seg.segmentType === 'work'" class="el-col-xs-24 row d-flex flex-wrap">
                <div class="col d-flex el-col-xs-12 align-items-center">
                  <h1 class="signPresenceHeader">
                    <font-awesome-icon class="iconNav"
                      :icon="seg.inType === 'resume' ? 'arrows-rotate' : 'sign-in-alt'" />
                  </h1>
                  <span class="mx-2 text-presence-today">
                    <b>{{ seg.inType === 'entry' ? 'ENTRADA' : 'REANUDACIÓN' }}</b>
                  </span>
                  <span class="text-presence-today">{{ formatTimeWithH(seg.in) }}</span>
                </div>
                <div class="col d-flex el-col-xs-12 align-items-center justify-content-end" v-if="seg.out">
                  <h1 class="signPresenceHeader">
                    <font-awesome-icon class="iconNav"
                      :icon="seg.outType === 'pause' ? pauseReasonIcon(seg.outReason) : 'door-open'" />
                  </h1>
                  <span class="mx-2 text-presence-today">
                    <b>{{ seg.outType === 'exit' ? 'SALIDA' : 'PARADA' }}</b>
                  </span>
                  <span class="text-presence-today">{{ formatTimeWithH(seg.out) }}</span>
                </div>
              </el-row>
              <el-row v-else-if="seg.segmentType === 'pause'"
                class="el-col-xs-24 row d-flex flex-wrap presence-header-pause-row">
                <div class="col d-flex el-col-xs-12 align-items-center">
                  <h1 class="signPresenceHeader">
                    <font-awesome-icon class="iconNav" :icon="pauseReasonIcon(seg.reason)" aria-hidden="true" />
                  </h1>
                  <span class="mx-2 text-presence-today">
                    <b>{{ pauseReasonDescription(seg.reason, [seg.otherText]) }}</b>
                  </span>
                  <span class="text-presence-today">{{ formatTimeWithH(seg.start) }}</span>
                </div>
                <div class="col d-flex el-col-xs-12 align-items-center justify-content-end" v-if="seg.end">
                  <h1 class="signPresenceHeader">
                    <font-awesome-icon class="iconNav" icon="arrows-rotate" />
                  </h1>
                  <span class="mx-2 text-presence-today"><b>REANUDACIÓN</b></span>
                  <span class="text-presence-today">{{ formatTimeWithH(seg.end) }}</span>
                </div>
              </el-row>
            </template>
            <div class="el-col-xs-24 row d-flex buttons-presence-header">
              <div class="d-flex flex-wrap justify-content-center ml-auto">
                <button :class="signButton ? 'btn_yellow' : 'btn_red'" class="btn btn-block m-2 btn-action"
                  @click="addSignUp()" :disabled="waitingSignUp || !stopButton" v-if="!isAccessedAdmin">
                  {{ signButton ? "EMPEZAR" : "TERMINAR" }}
                </button>
                <button :class="stopButton ? 'btn_orange' : 'btn_lime'" class="btn btn-block btn-action m-2"
                  v-if="!isAccessedAdmin" :disabled="waitingSignUp || signButton" @click="stopSignIn()">
                  {{
                    stopButton ? 'PARAR' :
                      'REANUDAR'
                  }}
                </button>
                <button class="btn btn-block m-2 btn-action btn_yellow" @click="openSeeChanges()">
                  {{ isAccessedAdmin ? 'REVISAR CAMBIOS' : 'CAMBIOS PENDIENTES' }}
                </button>
                <slot name="header-actions" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Total hoy -->
      <el-row class="total-presence" :class="{ 'total-presence-with-break': todayPauseSummary.length }">
        <div class="card card-notice1 w-100 pt-2 px-2 mt-2">
          <div class="d-flex align-items-center">
            <div class="d-flex align-items-center mr-4 time-presence presence-worked">
              <svg id="chronometer2" version="1.1" xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                <g>
                  <path
                    d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                      c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                      l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                      C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                      c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                      c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                      s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                      c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                      M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                      S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                      c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                  <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                      C124.383,80.339,121.025,76.981,116.883,76.981z" />
                </g>
              </svg>
              <h6 class="mx-2">
                <b>TRABAJADO</b>
              </h6>
              <h1>{{ secondsToTime(workedTimeToday) }}</h1>
            </div>
            <div class="mr-4 time-presence presence-break presence-pause-totals" v-if="todayPauseSummary.length">
              <div v-for="pause in todayPauseSummary" :key="pause.reason" class="presence-pause-metric">
                <el-tooltip :content="pauseReasonDescription(pause.reason, pause.texts)" placement="top">
                  <font-awesome-icon class="iconNav" :icon="pauseReasonIcon(pause.reason)"
                    :aria-label="pauseReasonDescription(pause.reason, pause.texts)" />
                </el-tooltip>
                <h1>{{ secondsToTime(pause.duration) }}</h1>
              </div>
            </div>
            <div class="d-flex align-items-center mr-4 time-presence presence-estimated">
              <svg id="chronometer2" version="1.1" xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                <g>
                  <path
                    d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                      c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                      l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                      C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                      c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                      c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                      s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                      c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                      M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                      S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                      c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                  <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                      C124.383,80.339,121.025,76.981,116.883,76.981z" />
                </g>
              </svg>
              <h6 class="mx-2">
                <b>JORNADA DE<br>CONVENIO</b>
              </h6>
              <h1>{{ secondsToTime(sumEstimatedTimeToday) }}</h1>
            </div>
            <div class="d-flex align-items-center mr-4 dif time-presence presence-diff"
              :class="{ 'presence-diff-positive': isPositiveDuration(diffTimeToday) }">
              <svg id="chronometer-dif" version="1.1" xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                <g>
                  <path
                    d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                      c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                      l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                      C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                      c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                      c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                      s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                      c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                      M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                      S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                      c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                  <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                      C124.383,80.339,121.025,76.981,116.883,76.981z" />
                </g>
              </svg>
              <h6 class="mx-2">
                <b>DIFERENCIA</b>
              </h6>
              <h1>{{ secondsToTime(diffTimeToday) }}</h1>
            </div>
            <div class="d-flex align-items-center date-presence presence-date justify-content-end">
              <div>
                <svg id="calendar-icon" version="1.1" xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="124.813px" height="124.813px"
                  viewBox="0 0 124.813 124.813" style="enable-background: new 0 0 124.813 124.813" xml:space="preserve">
                  <g>
                    <g>
                      <path
                        d="M48.083,80.355l-1.915,11.374c-0.261,1.555,0.377,3.122,1.65,4.05c1.275,0.926,2.968,1.05,4.361,0.32l10.226-5.338
                                                              L72.631,96.1c0.605,0.314,1.268,0.472,1.924,0.472c0.859,0,1.716-0.269,2.439-0.792c1.274-0.928,1.914-2.495,1.651-4.05
                                                              l-1.913-11.374l8.234-8.077c1.126-1.103,1.527-2.749,1.044-4.247c-0.485-1.497-1.783-2.593-3.341-2.823l-11.41-1.692
                                                              l-5.139-10.329c-0.697-1.41-2.141-2.303-3.716-2.303c-1.572,0-3.015,0.893-3.718,2.303l-5.134,10.329l-11.41,1.691
                                                              c-1.561,0.23-2.853,1.326-3.339,2.823c-0.486,1.498-0.086,3.146,1.042,4.247L48.083,80.355z" />
                      <path
                        d="M111.443,13.269H98.378V6.022C98.378,2.696,95.682,0,92.355,0H91.4c-3.326,0-6.021,2.696-6.021,6.022v7.247H39.282V6.022
                                                              C39.282,2.696,36.586,0,33.261,0h-0.956c-3.326,0-6.021,2.696-6.021,6.022v7.247H13.371c-6.833,0-12.394,5.559-12.394,12.394
                                                              v86.757c0,6.831,5.561,12.394,12.394,12.394h98.073c6.832,0,12.394-5.562,12.394-12.394V25.663
                                                              C123.837,18.828,118.275,13.269,111.443,13.269z M109.826,110.803H14.988V43.268h94.838V110.803z" />
                    </g>
                  </g>
                </svg>
              </div>

              <div class="mx-2 text-mute text-calendar">
                {{ moment(now).format('DD/MM/YYYY') }}
              </div>
            </div>
          </div>
        </div>
      </el-row>
      <el-row class="total-presence-mobile">
        <el-card class="card-presence">
          <template #header>
            <div class="row d-flex justify-content-between align-items-center card-presence-header ">
              <div class="el-col-xs-14">
                <div class="mx-2 text-title-header">
                  {{ moment(now).format('dddd D MMMM') }}
                </div>
              </div>
              <div class="el-col-xs-10 d-flex justify-content-end ">
                <div class="d-flex align-items-center">
                  <span class="mx-2 text-title-header">
                    Diferencia:
                  </span>
                  <span class="diffTimeMobile"
                    :class="{ 'presence-diff-positive': isPositiveDuration(diffTimeToday), 'presence-diff-negative': isNegativeDuration(diffTimeToday) }">{{
                      secondsToTime(diffTimeToday) }}</span>
                </div>
              </div>
            </div>
          </template>
          <template #default>
            <div class="row d-flex card-presence-body">
              <div class="el-col-xs-12 presence-mobile-total-item">
                <span class="text-title-body">Trabajado </span>
                <span class="text-title-time">{{ secondsToTime(workedTimeToday) }}</span>
              </div>
              <div class="el-col-xs-12 presence-mobile-total-item justify-content-end">
                <span class="text-title-body">Jornada de convenio </span>
                <span class="text-title-time">{{ secondsToTime(sumEstimatedTimeToday) }}</span>
              </div>
              <div v-for="pause in todayPauseSummary" :key="pause.reason"
                class="el-col-xs-24 presence-mobile-total-item presence-pause-mobile mt-2">
                <font-awesome-icon :icon="pauseReasonIcon(pause.reason)" aria-hidden="true" />
                <span class="text-title-body">{{ pauseReasonDescription(pause.reason, pause.texts) }}</span>
                <span class="text-title-time">{{ secondsToTime(pause.duration) }}</span>
              </div>
            </div>
          </template>
        </el-card>
      </el-row>

      <!-- Total fechas seleccionadas -->
      <el-row class="total-presence" :class="{ 'total-presence-with-break': pauseSummary.length }">
        <div class="card card-notice1 w-100 pt-2 px-2">
          <div class="d-flex align-items-center">
            <div class="d-flex align-items-center mr-4 time-presence presence-worked">
              <svg id="chronometer2" version="1.1" xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                <g>
                  <path
                    d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                      c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                      l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                      C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                      c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                      c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                      s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                      c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                      M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                      S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                      c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                  <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                      C124.383,80.339,121.025,76.981,116.883,76.981z" />
                </g>
              </svg>
              <h6 class="mx-2">
                <b>TRABAJADO</b>
              </h6>
              <h1>{{ secondsToTime(workedTimeTotal) }}</h1>
            </div>
            <div class="mr-4 time-presence presence-break presence-pause-totals" v-if="pauseSummary.length">
              <div v-for="pause in pauseSummary" :key="pause.reason" class="presence-pause-metric">
                <el-tooltip :content="pauseReasonDescription(pause.reason, pause.texts)" placement="top">
                  <font-awesome-icon class="iconNav" :icon="pauseReasonIcon(pause.reason)"
                    :aria-label="pauseReasonDescription(pause.reason, pause.texts)" />
                </el-tooltip>
                <h1>{{ secondsToTime(pause.duration) }}</h1>
              </div>
            </div>
            <div class="d-flex align-items-center mr-4 time-presence presence-estimated">
              <svg id="chronometer2" version="1.1" xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                <g>
                  <path
                    d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                      c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                      l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                      C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                      c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                      c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                      s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                      c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                      M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                      S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                      c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                  <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                      C124.383,80.339,121.025,76.981,116.883,76.981z" />
                </g>
              </svg>
              <h6 class="mx-2">
                <b>JORNADA DE<br>CONVENIO</b>
              </h6>
              <h1>{{ secondsToTime(sumEstimatedTime) }}</h1>
            </div>
            <div class="d-flex align-items-center mr-4 dif time-presence presence-diff"
              :class="{ 'presence-diff-positive': isPositiveDuration(diffTime) }">
              <svg id="chronometer-dif" version="1.1" xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                <g>
                  <path
                    d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                      c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                      l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                      C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                      c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                      c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                      s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                      c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                      M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                      S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                      c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                  <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                      C124.383,80.339,121.025,76.981,116.883,76.981z" />
                </g>
              </svg>
              <h6 class="mx-2">
                <b>DIFERENCIA</b>
              </h6>
              <h1>{{ secondsToTime(diffTime) }}</h1>
            </div>
            <div class="d-flex align-items-center ml-auto date-presence presence-date justify-content-end">
              <div>
                <svg id="calendar-icon" version="1.1" xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="124.813px" height="124.813px"
                  viewBox="0 0 124.813 124.813" style="enable-background: new 0 0 124.813 124.813" xml:space="preserve">
                  <g>
                    <g>
                      <path
                        d="M48.083,80.355l-1.915,11.374c-0.261,1.555,0.377,3.122,1.65,4.05c1.275,0.926,2.968,1.05,4.361,0.32l10.226-5.338
                                                              L72.631,96.1c0.605,0.314,1.268,0.472,1.924,0.472c0.859,0,1.716-0.269,2.439-0.792c1.274-0.928,1.914-2.495,1.651-4.05
                                                              l-1.913-11.374l8.234-8.077c1.126-1.103,1.527-2.749,1.044-4.247c-0.485-1.497-1.783-2.593-3.341-2.823l-11.41-1.692
                                                              l-5.139-10.329c-0.697-1.41-2.141-2.303-3.716-2.303c-1.572,0-3.015,0.893-3.718,2.303l-5.134,10.329l-11.41,1.691
                                                              c-1.561,0.23-2.853,1.326-3.339,2.823c-0.486,1.498-0.086,3.146,1.042,4.247L48.083,80.355z" />
                      <path
                        d="M111.443,13.269H98.378V6.022C98.378,2.696,95.682,0,92.355,0H91.4c-3.326,0-6.021,2.696-6.021,6.022v7.247H39.282V6.022
                                                              C39.282,2.696,36.586,0,33.261,0h-0.956c-3.326,0-6.021,2.696-6.021,6.022v7.247H13.371c-6.833,0-12.394,5.559-12.394,12.394
                                                              v86.757c0,6.831,5.561,12.394,12.394,12.394h98.073c6.832,0,12.394-5.562,12.394-12.394V25.663
                                                              C123.837,18.828,118.275,13.269,111.443,13.269z M109.826,110.803H14.988V43.268h94.838V110.803z" />
                    </g>
                  </g>
                </svg>
              </div>

              <div class="mx-2 text-mute text-calendar">
                {{ currentDay }}
              </div>
            </div>
          </div>
        </div>
      </el-row>
      <el-row class="total-presence-mobile">
        <el-card class="card-presence">
          <template #header>
            <div class="row d-flex justify-content-between align-items-center card-presence-header ">
              <div class="el-col-xs-14">
                <div class="mx-2 text-title-header">
                  {{ currentDay }}
                </div>
              </div>
              <div class="el-col-xs-10 d-flex justify-content-end ">
                <div class="d-flex align-items-center">
                  <span class="mx-2 text-title-header">
                    Diferencia:
                  </span>
                  <span class="diffTimeMobile"
                    :class="{ 'presence-diff-positive': isPositiveDuration(diffTime), 'presence-diff-negative': isNegativeDuration(diffTime) }">{{
                      secondsToTime(diffTime) }}</span>
                </div>
              </div>
            </div>
          </template>
          <template #default>
            <div class="row d-flex card-presence-body">
              <div class="el-col-xs-12 presence-mobile-total-item">
                <span class="text-title-body">Trabajado </span>
                <span class="text-title-time">{{ secondsToTime(workedTimeTotal) }}</span>
              </div>
              <div class="el-col-xs-12 presence-mobile-total-item justify-content-end">
                <span class="text-title-body">Jornada de convenio </span>
                <span class="text-title-time">{{ secondsToTime(sumEstimatedTime) }}</span>
              </div>
              <div v-for="pause in pauseSummary" :key="pause.reason"
                class="el-col-xs-24 presence-mobile-total-item presence-pause-mobile mt-2">
                <font-awesome-icon :icon="pauseReasonIcon(pause.reason)" aria-hidden="true" />
                <span class="text-title-body">{{ pauseReasonDescription(pause.reason, pause.texts) }}</span>
                <span class="text-title-time">{{ secondsToTime(pause.duration) }}</span>
              </div>
            </div>
          </template>
        </el-card>
      </el-row>

      <!-- Selector fechas -->
      <el-row class="mt-5 d-flex align-items-center justify-content-start flex-wrap presence-filter-bar">
        <div class="d-flex align-items-center mr-2">
          <svg id="chronometer" version="1.1" xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
            style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
            <g>
              <path d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                            c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                            l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                            C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                            c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                            c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                            s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                            c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                            M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                            S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                            c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
              <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                            C124.383,80.339,121.025,76.981,116.883,76.981z" />
            </g>
          </svg>
          <div class="text-title">
            <b>Fichajes últimos días</b>
          </div>
        </div>
        <div id="date-filter" class="d-flex justify-content-end ml-auto mt-1">
          <!-- <shared-datepicker @blur="onchangeDateRange"></shared-datepicker> -->
          <el-date-picker v-model="daterange" type="daterange" start-placeholder="Fecha Inicio"
            class="datePickerNewPresence" end-placeholder="Fecha Fin" format="DD/MM/YYYY" value-format="YYYY-MM-DD"
            :picker-options="pickerOptions" :shortcuts="shortcuts" @change="onchangeDateRange()" :teleport="false"
            popper-class="custom-pickerPresence" />
        </div>
        <div class="presence-filter-actions">
          <button type="button" class="btn btn_yellow px-4 btn-add-day" @click="openNewPresence()">
            Añadir día
          </button>
        </div>
      </el-row>

      <!-- tabla fichajes -->
      <el-row>
        <div id="table-presence" class="table table-user mt-0 pt-0">
          <table>
            <thead class="card-head">
              <tr>
                <th>
                  <svg id="calendar-icon-table" version="1.1" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="124.813px" height="124.813px"
                    viewBox="0 0 124.813 124.813" style="enable-background: new 0 0 124.813 124.813"
                    xml:space="preserve">
                    <g>
                      <g>
                        <path
                          d="M48.083,80.355l-1.915,11.374c-0.261,1.555,0.377,3.122,1.65,4.05c1.275,0.926,2.968,1.05,4.361,0.32l10.226-5.338
                                                                L72.631,96.1c0.605,0.314,1.268,0.472,1.924,0.472c0.859,0,1.716-0.269,2.439-0.792c1.274-0.928,1.914-2.495,1.651-4.05
                                                                l-1.913-11.374l8.234-8.077c1.126-1.103,1.527-2.749,1.044-4.247c-0.485-1.497-1.783-2.593-3.341-2.823l-11.41-1.692
                                                                l-5.139-10.329c-0.697-1.41-2.141-2.303-3.716-2.303c-1.572,0-3.015,0.893-3.718,2.303l-5.134,10.329l-11.41,1.691
                                                                c-1.561,0.23-2.853,1.326-3.339,2.823c-0.486,1.498-0.086,3.146,1.042,4.247L48.083,80.355z" />
                        <path
                          d="M111.443,13.269H98.378V6.022C98.378,2.696,95.682,0,92.355,0H91.4c-3.326,0-6.021,2.696-6.021,6.022v7.247H39.282V6.022
                                                                C39.282,2.696,36.586,0,33.261,0h-0.956c-3.326,0-6.021,2.696-6.021,6.022v7.247H13.371c-6.833,0-12.394,5.559-12.394,12.394
                                                                v86.757c0,6.831,5.561,12.394,12.394,12.394h98.073c6.832,0,12.394-5.562,12.394-12.394V25.663
                                                                C123.837,18.828,118.275,13.269,111.443,13.269z M109.826,110.803H14.988V43.268h94.838V110.803z" />
                      </g>
                    </g>
                  </svg>
                  DIA
                </th>
                <th>
                  <svg id="lamp-icon" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 315 315"
                    xmlns:xlink="http://www.w3.org/1999/xlink" enable-background="new 0 0 315 315">
                    <path
                      d="m285.61,145.248l-61.643-133.682c-3.041-6.593-10.781-11.566-18.005-11.566h-96.924c-7.224,0-14.964,4.973-18.005,11.566l-61.643,133.682c-1.932,4.188-1.771,8.57 0.438,12.022 2.199,3.437 6.082,5.407 10.651,5.407h110.021v83.243c-34.008,3-62.089,27.654-68.104,60.83-0.37,2.042 0.185,4.143 1.515,5.735 1.33,1.593 3.298,2.514 5.373,2.514h136.432c2.075,0 4.043-0.921 5.373-2.514 1.33-1.593 1.885-3.693 1.515-5.735-6.016-33.175-34.097-57.829-68.104-60.83v-83.243h46.644v27.861c-8.034,2.903-13.807,10.624-13.807,19.697 0,11.549 9.334,20.944 20.807,20.944 11.474,0 20.808-9.395 20.808-20.944 0-9.073-5.773-16.795-13.808-19.697v-27.861h49.378c4.569,0 8.452-1.971 10.651-5.407 2.209-3.452 2.369-7.834 0.437-12.022zm-68.904,155.752h-118.412c8.212-24.42 31.867-41.389 59.205-41.389s50.994,16.969 59.207,41.389zm1.438-83.819c-3.753,0-6.807-3.115-6.807-6.944 0-3.828 3.054-6.942 6.807-6.942 3.754,0 6.808,3.114 6.808,6.942-0.001,3.828-3.054,6.944-6.808,6.944zm-174.919-68.503l60.521-131.248c0.754-1.635 3.523-3.43 5.292-3.43h96.924c1.769,0 4.538,1.795 5.292,3.43l60.521,131.248h-228.55z" />
                  </svg>
                  FICHAJES
                </th>
                <th>
                  <svg id="chronometer3" version="1.1" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                    style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                    <g>
                      <path
                        d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                                      c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                                      l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                                      C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                                      c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                                      c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                                      s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                                      c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                                      M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                                      S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                                      c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                      <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                                      C124.383,80.339,121.025,76.981,116.883,76.981z" />
                    </g>
                  </svg>
                  TOTALES
                </th>
                <th>
                  DIFERENCIA
                </th>
                <th>
                  EDITAR
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sign, index) in signings.slice().reverse()" :key="index + sign">
                <td>
                  <svg id="calendar-icon-table-cell" version="1.1" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="124.813px" height="124.813px"
                    viewBox="0 0 124.813 124.813" style="enable-background: new 0 0 124.813 124.813"
                    xml:space="preserve">
                    <g>
                      <g>
                        <path
                          d="M48.083,80.355l-1.915,11.374c-0.261,1.555,0.377,3.122,1.65,4.05c1.275,0.926,2.968,1.05,4.361,0.32l10.226-5.338
                                                                L72.631,96.1c0.605,0.314,1.268,0.472,1.924,0.472c0.859,0,1.716-0.269,2.439-0.792c1.274-0.928,1.914-2.495,1.651-4.05
                                                                l-1.913-11.374l8.234-8.077c1.126-1.103,1.527-2.749,1.044-4.247c-0.485-1.497-1.783-2.593-3.341-2.823l-11.41-1.692
                                                                l-5.139-10.329c-0.697-1.41-2.141-2.303-3.716-2.303c-1.572,0-3.015,0.893-3.718,2.303l-5.134,10.329l-11.41,1.691
                                                                c-1.561,0.23-2.853,1.326-3.339,2.823c-0.486,1.498-0.086,3.146,1.042,4.247L48.083,80.355z" />
                        <path
                          d="M111.443,13.269H98.378V6.022C98.378,2.696,95.682,0,92.355,0H91.4c-3.326,0-6.021,2.696-6.021,6.022v7.247H39.282V6.022
                                                                C39.282,2.696,36.586,0,33.261,0h-0.956c-3.326,0-6.021,2.696-6.021,6.022v7.247H13.371c-6.833,0-12.394,5.559-12.394,12.394
                                                                v86.757c0,6.831,5.561,12.394,12.394,12.394h98.073c6.832,0,12.394-5.562,12.394-12.394V25.663
                                                                C123.837,18.828,118.275,13.269,111.443,13.269z M109.826,110.803H14.988V43.268h94.838V110.803z" />
                      </g>
                    </g>
                  </svg>
                  {{ formatPresenceDate(sign.date) }}
                </td>
                <td style="text-align: center">
                  <div v-for="(s, i) in sign.signs" :key="i + (s.entry ?? s.resume)" class="row">
                    <div class="col-md-3" />
                    <div class="col-md-1">
                      <font-awesome-icon v-if="s.entry" class="iconNav" icon="sign-in-alt" />
                      <font-awesome-icon v-else-if="s.resume" class="iconNav" icon="arrows-rotate" />
                    </div>
                    <div class="col-md-2">
                      <span>{{ formatTimeWithOutStyle(s.entry ?? s.resume) }}</span>
                    </div>

                    <div class="col-md-1">
                      <font-awesome-icon v-if="s.exit" class="iconNav" icon="door-open" />
                      <el-tooltip v-else-if="s.pause"
                        :content="pauseReasonDescription(s.pause.pauseReason, [s.pause.pauseReasonOtherText])" placement="top">
                        <font-awesome-icon class="iconNav" :icon="pauseReasonIcon(s.pause.pauseReason)"
                          :aria-label="pauseReasonDescription(s.pause.pauseReason, [s.pause.pauseReasonOtherText])" />
                      </el-tooltip>
                    </div>
                    <div class="col-md-2">
                      <span>{{ formatTimeWithOutStyle(s.exit ?? s.pause) }}</span>
                    </div>
                    <div class="col-md-3" />
                  </div>
                </td>
                <td>
                  <div class="presence-table-totals">
                    <div class="presence-table-total-item">
                      <font-awesome-icon class="iconNav" icon="briefcase" />
                      <strong>{{ secondsToTime(dailyBalances[sign.date].worked) }}</strong>
                    </div>
                    <div v-for="pause in getPauseSummary([sign])" :key="pause.reason" class="presence-table-total-item">
                      <el-tooltip :content="pauseReasonDescription(pause.reason, pause.texts)" placement="top">
                        <font-awesome-icon class="iconNav" :icon="pauseReasonIcon(pause.reason)"
                          :aria-label="pauseReasonDescription(pause.reason, pause.texts)" />
                      </el-tooltip>
                      <strong>{{ secondsToTime(pause.duration) }}</strong>
                    </div>
                    <div class="presence-table-total-item presence-table-real-total">
                      <span class="presence-table-total-label">Total</span>
                      <strong>{{ secondsToTime(dailyBalances[sign.date].total) }}</strong>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="presence-table-difference">
                    <el-tooltip :content="getDayBalanceHint(sign)" placement="top">
                      <span class="diffTimeMobile presence-day-difference"
                        :class="{ 'presence-diff-positive': isPositiveDuration(dailyDifferences[sign.date]), 'presence-diff-negative': isNegativeDuration(dailyDifferences[sign.date]) }">
                        {{ Number(dailyDifferences[sign.date]) >= 60000 ? '+' : '' }}{{
                          secondsToTime(dailyDifferences[sign.date]) }}
                      </span>
                    </el-tooltip>
                    <div v-if="dailyBalances[sign.date].extraRest >= 60 * 1000"
                      class="presence-difference-extra presence-rest-extra">
                      <strong>{{ secondsToTime(dailyBalances[sign.date].extraRest) }}</strong>
                      <span class="presence-table-total-label">Descanso extra</span>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="row justify-content-center align-center">
                    <div class="col-auto">
                      <shared-button-plus icon="eye" @click="openDialog(sign)" title="Editar" />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </el-row>
      <el-row class="table-presence-mobile" v-if="signings.length > 0">
        <div class="row d-flex justify-content-center">
          <div v-for="(sign, index) in signings.slice().reverse()" :key="index + sign" class="row d-flex mb-3">
            <el-card class="card-presence-item">
              <div class="row d-flex">
                <div class="el-col-xs-24 date-item-presence justify-content-center">
                  <svg id="calendar-icon-table-mobile" version="1.1" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="124.813px" height="124.813px"
                    viewBox="0 0 124.813 124.813" style="enable-background: new 0 0 124.813 124.813"
                    xml:space="preserve">
                    <g>
                      <g>
                        <path
                          d="M48.083,80.355l-1.915,11.374c-0.261,1.555,0.377,3.122,1.65,4.05c1.275,0.926,2.968,1.05,4.361,0.32l10.226-5.338
                                                                L72.631,96.1c0.605,0.314,1.268,0.472,1.924,0.472c0.859,0,1.716-0.269,2.439-0.792c1.274-0.928,1.914-2.495,1.651-4.05
                                                                l-1.913-11.374l8.234-8.077c1.126-1.103,1.527-2.749,1.044-4.247c-0.485-1.497-1.783-2.593-3.341-2.823l-11.41-1.692
                                                                l-5.139-10.329c-0.697-1.41-2.141-2.303-3.716-2.303c-1.572,0-3.015,0.893-3.718,2.303l-5.134,10.329l-11.41,1.691
                                                                c-1.561,0.23-2.853,1.326-3.339,2.823c-0.486,1.498-0.086,3.146,1.042,4.247L48.083,80.355z" />
                        <path
                          d="M111.443,13.269H98.378V6.022C98.378,2.696,95.682,0,92.355,0H91.4c-3.326,0-6.021,2.696-6.021,6.022v7.247H39.282V6.022
                                                                C39.282,2.696,36.586,0,33.261,0h-0.956c-3.326,0-6.021,2.696-6.021,6.022v7.247H13.371c-6.833,0-12.394,5.559-12.394,12.394
                                                                v86.757c0,6.831,5.561,12.394,12.394,12.394h98.073c6.832,0,12.394-5.562,12.394-12.394V25.663
                                                                C123.837,18.828,118.275,13.269,111.443,13.269z M109.826,110.803H14.988V43.268h94.838V110.803z" />
                      </g>
                    </g>
                  </svg>
                  {{ formatPresenceDate(sign.date) }}
                </div>
                <div class="el-col-xs-24 row d-flex item-presence" v-for="(s, i) in sign.signs"
                  :key="i + (s.entry ?? s.resume)">
                  <div class="el-col-xs-12">
                    <font-awesome-icon v-if="s.entry" class="iconNav" icon="sign-in-alt" />
                    <font-awesome-icon v-else-if="s.resume" class="iconNav" icon="arrows-rotate" />
                    <span class="time-presence-item"> {{ formatTimeWithOutStyle(s.entry ?? s.resume) }}</span>
                  </div>
                  <div class="el-col-xs-12">
                    <font-awesome-icon v-if="s.exit" class="iconNav" icon="door-open" />
                    <el-tooltip v-else-if="s.pause"
                      :content="pauseReasonDescription(s.pause.pauseReason, [s.pause.pauseReasonOtherText])" placement="top">
                      <font-awesome-icon class="iconNav" :icon="pauseReasonIcon(s.pause.pauseReason)"
                        :aria-label="pauseReasonDescription(s.pause.pauseReason, [s.pause.pauseReasonOtherText])" />
                    </el-tooltip>
                    <span class="time-presence-item"> {{ formatTimeWithOutStyle(s.exit ?? s.pause) }}</span>
                  </div>
                </div>
                <div class="el-col-xs-20 worked-item-presence d-flex align-items-center">
                  <font-awesome-icon class="iconNav worked-item-presence-icon" icon="briefcase" />
                  <span class="worked-item-presence-text">Trabajado</span>
                  <span class="worked-item-presence-total">{{ secondsToTime(dailyBalances[sign.date].worked)
                  }}</span>
                </div>
                <div v-for="pause in getPauseSummary([sign])" :key="pause.reason"
                  class="el-col-xs-20 worked-item-presence presence-pause-mobile d-flex align-items-center">
                  <font-awesome-icon class="iconNav worked-item-presence-icon" :icon="pauseReasonIcon(pause.reason)" />
                  <span class="worked-item-presence-text">{{ pauseReasonDescription(pause.reason, pause.texts) }}</span>
                  <span class="worked-item-presence-total">{{ secondsToTime(pause.duration) }}</span>
                </div>
                <div class="el-col-xs-24 worked-item-presence d-flex align-items-center">
                  <span class="worked-item-presence-text">Total</span>
                  <span class="worked-item-presence-total">{{ secondsToTime(dailyBalances[sign.date].total) }}</span>
                </div>
                <div class="el-col-xs-24 worked-item-presence d-flex align-items-center">
                  <span class="worked-item-presence-text">Diferencia</span>
                  <el-tooltip :content="getDayBalanceHint(sign)" placement="top">
                    <span class="worked-item-presence-total diffTimeMobile presence-day-difference"
                      :class="{ 'presence-diff-positive': isPositiveDuration(dailyDifferences[sign.date]), 'presence-diff-negative': isNegativeDuration(dailyDifferences[sign.date]) }">
                      {{ Number(dailyDifferences[sign.date]) >= 60000 ? '+' : '' }}{{
                        secondsToTime(dailyDifferences[sign.date]) }}
                    </span>
                  </el-tooltip>
                </div>
                <div v-if="dailyBalances[sign.date].extraRest >= 60 * 1000"
                  class="el-col-xs-24 worked-item-presence presence-rest-extra d-flex align-items-center">
                  <span class="worked-item-presence-text">Descanso extra</span>
                  <span class="worked-item-presence-total">{{ secondsToTime(dailyBalances[sign.date].extraRest)
                    }}</span>
                </div>
                <div class="el-col-xs-4 justify-content-end">
                  <shared-button-plus icon="eye" @click="openDialog(sign)" title="Editar" />
                </div>
              </div>
            </el-card>
          </div>
        </div>
      </el-row>
    </div>
    <!-- Modal editar -->
    <el-dialog v-model="showModalChangePresence" @close="closeModal()" width="550" :center="true" :lock-scroll="true"
      style="border-radius: 20px;" :show-close="false" class="el-dialog el-dialog-presence-edit">
      <div class="my-header">
        <svg id="asterisk" xmlns="http://www.w3.org/2000/svg" width="700" height="1024" viewBox="0 0 724 1024">
          <path
            d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
        </svg>
        <span class="text-header ml-5"> Presencia</span>
        <img class="close-icon pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt="" @click="closeModal()">
      </div>
      <PresenceUserDialog v-if="showModalChangePresence" :presenceSigns="selectedSign" @close-dialog="closeModal"
        @change-presence="closeModal" />
    </el-dialog>

    <!-- Modal Nueva fecha-->
    <el-dialog v-model="showModalNewPresence" @close="closeNewModal()" width="550" :center="true" :lock-scroll="true"
      style="border-radius: 20px;" :show-close="false" class="el-dialog-presence-new">
      <div class="my-header">
        <svg id="asterisk" xmlns="http://www.w3.org/2000/svg" width="700" height="1024" viewBox="0 0 724 1024">
          <path
            d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
        </svg>
        <span class="text-header ml-5"> Presencia</span>
        <img class="close-icon pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
          @click="closeNewModal()">
      </div>
      <div class="row d-flex" style="padding-bottom: 10px;">
        <div class="col-7 new-presence-date">
          <el-date-picker v-model="newDatePresence" type="date" class="datePickerNewPresence"
            placeholder="Inserte una fecha" style="width: 100%;" :disabled-date="disableFutureDates" />
        </div>
        <div class="col-5 new-presence-button" style="margin-left: -10px;">
          <button class="btn btn_yellow px-4" @click="setNewDay()">
            Nueva presencia
          </button>
        </div>
        <div class="col-7">
          <p v-if="errorDatePickerNewPresence" class="invalid-feedback mb-0">
            <small>
              Inserte una fecha para continuar
            </small>
          </p>
        </div>
      </div>
      <PresenceUserDialog v-if="canChangeNewDay" :presenceSigns="newSigning" @close-dialog="closeNewModal"
        @change-presence="closeNewModal" />
    </el-dialog>

    <!-- Modal Revisar Cambios -->
    <el-dialog v-model="showModalSeeChanges" width="600" :center="true" :lock-scroll="true" :show-close="false"
      style="border-radius: 20px;" @close="closeSeeChanges()" class="el-dialog-presence-changes">
      <PresenceDialogChanges :isAccessedAdmin="isAccessedAdmin" :presesenceChanges="totalRecords"
        v-if="showModalSeeChanges" @close="closeSeeChanges()" @finish="finishDialogChanges()" />
    </el-dialog>

    <!-- Modal Pausar Fichaje -->
    <StopSignInModal v-model="modalStopSignIn" @confirm="confirmDialogModalStopSignIn" @close="waitingSignUp = false" />

  </div>
</template>

<style scoped lang="scss">
@import "../../assets/sass/styles.scss";

// Las filas comparten las columnas de icono, etiqueta y hora. El ancho de
// cada etiqueta lo determina el texto más largo de esa columna.
.header {
  container: presence-header / inline-size;
}

.presence-segments {
  display: grid;
  grid-template-columns: repeat(2, 44px max-content minmax(0, 1fr)) 44px max-content max-content;
  column-gap: 12px;

  >.el-row,
  >.el-row>.card,
  >.el-row>.card>.d-flex {
    display: grid !important;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    min-width: 0;
  }

  >.el-row>.card>.d-flex>.col {
    display: grid !important;
    grid-template-columns: subgrid;
    grid-column: span 3;
    align-items: center;
    min-width: 0;

    >h6 {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    >h1:first-child {
      text-align: center;
    }

    >h1:last-child {
      white-space: nowrap;
    }
  }
}

// En paneles estrechos se mantienen los tres bloques en una única línea,
// reduciendo las horas y el espacio entre columnas según el ancho disponible.
@container presence-header (max-width: 1100px) {
  .presence-segments {
    grid-template-columns: repeat(2, 32px fit-content(120px) minmax(0, 1fr)) 32px max-content max-content;
    column-gap: 6px;

    >.el-row>.card>.d-flex>.col {
      >h1:last-child {
        font-size: clamp(18px, 3cqi, 32px);
      }

      >h1:first-child {
        font-size: 28px;
      }

      >svg {
        max-width: 32px;
      }

      >h6 {
        font-size: clamp(10px, 1.25cqi, 14px);
        overflow-wrap: anywhere;
      }
    }
  }
}

@container presence-header (max-width: 800px) {
  .presence-segments {
    grid-template-columns: repeat(2, 24px fit-content(90px) minmax(0, 1fr)) 24px max-content max-content;
    column-gap: 4px;

    >.el-row>.card>.d-flex>.col {
      >h1:first-child {
        font-size: 22px;
      }

      >svg {
        max-width: 24px;
      }
    }
  }
}

.presence-pause-totals {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.presence-pause-metric {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.presence-pause-mobile {
  flex-wrap: wrap;
  gap: 6px;

  .text-title-body,
  .worked-item-presence-text {
    overflow-wrap: anywhere;
    min-width: 0;
  }
}

.presence-filter-bar {
  gap: 12px;
  width: 100%;

  #date-filter {
    flex: 1 1 300px;
    min-width: 0;
    margin: 0 0 0 auto !important;

    :deep(.el-date-editor--daterange) {
      width: 100%;
      min-height: 44px;
      border-radius: 20px;
      box-shadow: 0 0 0 1px #000 inset;
    }

    :deep(.el-date-editor--daterange:hover),
    :deep(.el-date-editor--daterange.is-active) {
      box-shadow: 0 0 0 1px #409eff inset;
    }
  }

  .presence-filter-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    margin: 0 !important;
  }
}

@media (max-width: 767px) {
  .presence-filter-bar {

    >div,
    #date-filter,
    .presence-filter-actions {
      width: 100%;
      flex: 1 1 100%;
    }

    .presence-filter-actions {
      flex-direction: column;
    }

    .btn {
      width: 100%;
      max-width: 100%;
    }
  }
}


.content {
  .table-fichaje {
    th {
      text-align: left;
      vertical-align: center;
      padding-left: 5px;
      color: #474ce6;
      background-color: white;
    }

    td {
      margin-top: 10px;
      border-bottom: 1px solid #ddd;
    }

    .card-head {
      border: 1px solid rgba(0, 0, 0, 0.125);
      height: 3.8em;
      border-radius: 1.8em;

      box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
    }
  }

  .card-notice {
    border-radius: 0.8em;
    color: white;
    background-color: #474ce6;
  }

  .card-notice1 {
    border-radius: 0.8em;
    color: #474ce6;
  }

  .presence-header-pause-card {
    background-color: #fb8e00;
  }

  .dif {
    color: #f8309a;
  }

  .text-title {
    margin-left: 5px;
    margin-top: 5px;
    width: 100%;
    height: auto;
    color: #474ce6;
    font-size: 2.5em;
    font-weight: 600;
    letter-spacing: -0.05em;
    text-size-adjust: 50%;
  }

  .text-calendar {
    color: rgb(172, 165, 165);
    font-weight: bold;
  }

  #lamp-icon {
    fill: $bg-blue;
    width: 30px;
  }

  #calendar-icon-table-cell {
    margin-right: 5px;
    fill: black;
    width: 18px;
    height: 18px;
    padding-bottom: 3px;
  }

  #calendar-icon-table {
    fill: $bg-blue;
    width: 20px;
    height: 20px;
  }

  #calendar-icon {
    fill: rgb(172, 165, 165);
    width: 1em;
    height: 1.5em;
    padding-bottom: 3px;
  }

  #chronometer3 {
    fill: $bg-blue;
    width: 35px;
  }

  #chronometer {
    fill: $bg-blue;
    width: 35px;
    //margin-top: 10px;
  }

  #chronometer2 {
    fill: $bg-blue;
    width: 2.9em;
    padding-bottom: 8px;
  }

  #chronometer-dif {
    fill: #f8309a;
    width: 2.9em;
    padding-bottom: 8px;
  }

  #chronometer1 {
    fill: white;
    width: 2.9em;
    padding-bottom: 8px;
  }

  :deep(.btn-action) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    box-sizing: border-box;
    width: 15rem;
    max-width: calc(100% - 1rem);
    height: 3rem;
    font-size: 1rem;
    font-weight: bold;
  }
}

.title-card {
  display: flex;
  flex-flow: nowrap;
  justify-content: space-between;
  flex-direction: row;
  position: absolute;
  top: 14em;
}

#Presence {
  padding: 0em;
  padding-right: 5em;
  transition: 0.6s;

  .title-button {
    display: flex;
    align-items: center;

    h2 {
      color: #5a5c69;
      font-weight: 400;
      line-height: 1.2;
      font-size: 28px;
      margin: 0;
      margin-right: 30px;
    }

    button {
      color: #fff;
      border: 0;
      font-weight: 400;
      text-align: center;
      padding: 8px;
      font-size: 18px;
      width: 130px;
      border-radius: 8px;
    }

    .btn-on {
      background-color: red;
    }

    .btn-off {
      background-color: green;
    }
  }

  .total-cards {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .card1 {
      width: 70%;

      .container-ficha {
        border-radius: 10px;
        margin-top: 35px;
        width: 100%;
        -webkit-box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15) !important;
        box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15) !important;

        .title-ficha {
          padding: 0.75rem 1.25rem;
          margin-bottom: 0;
          background-color: #f8f9fc;
          border-bottom: 1px solid #e3e6f0;

          h4 {
            color: #4e73df;
            font-weight: 700;
            margin-bottom: 0;
          }
        }

        .table-ficha {
          padding: 30px;
          background-color: white;

          table {
            border: 1px solid #e3e6f0;
            width: 100%;
            color: #858796;

            th {
              border: 1px solid #e3e6f0;
              padding: 12px;
              padding-left: 20px;
              font-size: 20px;
            }

            td {
              border: 1px solid #e3e6f0;
              padding: 12px;
              padding-left: 20px;
              background-color: white;
              font-size: 18px;
            }

            tr {
              &:nth-child(even) {
                td {
                  background-color: #ececec;
                }
              }
            }
          }
        }

        .table-all {
          table {
            th {
              text-align: center;
            }

            td {
              padding: 8px;
              text-align: center;
            }
          }
        }
      }
    }

    .card2 {
      width: 28%;

      .container-contador {
        -webkit-box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15) !important;
        box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15) !important;
        background-color: white;
        margin-top: 35px;
        padding: 20px;
        border-radius: 10px;

        .iconNav {
          color: #dddfeb;
          font-size: 30px;
        }
      }

      .contador-blue {
        border-left: 5px solid #4e73df;
        display: flex;
        align-items: center;
        justify-content: space-between;

        h4 {
          color: #4e73df;
          font-size: 16px;
        }

        div {
          font-size: 24px;
          font-weight: bold;
        }
      }

      .contador-green {
        border-left: 5px solid #1cc88a;
        display: flex;
        align-items: center;
        justify-content: space-between;

        h4 {
          color: #1cc88a;
          font-size: 16px;
        }

        div {
          font-size: 24px;
          font-weight: bold;
        }
      }
    }
  }

  .footer-undanet {
    padding: 2rem 0;
    -ms-flex-negative: 0;
    flex-shrink: 0;
    position: absolute;
    bottom: 0;
    width: 100%;
    left: 0;
    right: 0;
    text-align: center;
  }
}

.my-header {
  margin-top: 2px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.text-header {
  text-align: left;
  font-size: 4em;
  font-weight: 900;
  letter-spacing: -0.05em;
  color: #f9156f;
  margin-left: 30px;
}

.header-subtitle {
  font-weight: 800;
  font-size: 40px;
  margin-left: 10px;
  color: $font-blue-color;
}

.subtitle-header {
  #chronometer {
    fill: $bg-blue;
    width: 2.9em;
    padding-bottom: 21px;
  }
}

.date-presence {
  flex: 0 0 150px;
  margin-left: 0 !important;
  min-width: 118px;
  width: auto;
}

.time-presence {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
}

.presence-day-difference {
  white-space: nowrap;
}

.diffTimeMobile {
  color: #474ce6 !important;

  &.presence-diff-positive {
    color: #28a745 !important;
  }

  &.presence-diff-negative {
    color: #f9156f !important;
  }
}

.presence-diff-positive {

  h6,
  h1,
  span,
  svg {
    color: #28a745 !important;
    fill: #28a745 !important;
  }
}

.total-presence {
  .card-notice1 {
    overflow: hidden;

    >.d-flex {
      flex-wrap: nowrap;
      gap: 8px 12px;
      justify-content: space-between;
    }
  }

  .time-presence {
    margin-right: 0 !important;
  }

  .presence-worked {
    justify-content: flex-start;
  }

  .presence-break,
  .presence-estimated,
  .presence-diff {
    justify-content: center;
  }

  .presence-date {
    gap: 4px;
    justify-content: flex-end;

    >div {
      flex-shrink: 0;
    }

    .text-calendar {
      margin-left: 0.25rem !important;
      margin-right: 0 !important;
      max-width: clamp(120px, 16vw, 240px);
      text-align: right;
    }
  }

  h1 {
    font-size: clamp(1.2rem, 1.65vw, 1.85rem);
    margin-bottom: 0;
    white-space: nowrap;
  }

  h6 {
    font-size: clamp(0.72rem, 0.85vw, 1rem);
    margin-bottom: 0;
    white-space: nowrap;
  }

  svg {
    flex-shrink: 0;
  }

  #chronometer2,
  #chronometer-dif {
    width: clamp(1.8em, 2.2vw, 2.45em);
  }

  #calendar-icon {
    width: 0.72em;
    height: 1.05em;
  }

  .iconNav {
    font-size: clamp(1rem, 1.35vw, 1.35rem);
  }

  .text-calendar {
    font-size: clamp(0.68rem, 0.85vw, 0.9rem);
    line-height: 1.1;
    overflow-wrap: normal;
    white-space: normal;
  }
}

.total-presence-mobile {
  width: 100%;

  .card-presence {
    width: 100%;
  }

  .card-presence-body {
    row-gap: 8px;
  }

  .presence-mobile-total-item {
    align-items: center;
    display: flex;
    gap: 8px;
  }
}

.presence-table-totals {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.presence-table-difference {
  display: grid;
  grid-template-columns: max-content minmax(0, max-content);
  align-items: center;
  justify-content: start;
  text-align: left;
  gap: 4px 8px;

  >.presence-day-difference {
    grid-column: 1;
    text-align: left;
  }
}

.presence-difference-extra {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;

  strong {
    font-weight: 400;
    text-align: left;
    white-space: nowrap;
  }
}

.presence-rest-extra {
  color: #f9156f !important;

  strong,
  span {
    color: #f9156f !important;
  }
}

.presence-table-total-label {
  font-size: 0.8rem;
  white-space: normal;
}

.presence-table-real-total {
  border-top: 1px solid #e4e7ed;
  padding-top: 4px;

  strong {
    font-weight: 700;
  }
}

.presence-table-total-item {
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: center;
  white-space: nowrap;

  strong {
    color: #000;
    font-weight: 400;
  }
}

@media (min-width: 768px) and (max-width: 1150px) {
  .total-presence {
    .card-notice1 {
      position: relative;

      >.d-flex {
        display: grid !important;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        grid-template-areas:
          "worked estimated"
          "diff date";
        column-gap: clamp(28px, 8vw, 120px);
        row-gap: 12px;
      }
    }

    .presence-worked {
      grid-area: worked;
    }

    .presence-estimated {
      grid-area: estimated;
    }

    .presence-diff {
      grid-area: diff;
    }

    .presence-date {
      grid-area: date;
    }

    .presence-worked,
    .presence-break,
    .presence-estimated,
    .presence-diff {
      justify-content: flex-start;
      width: 100%;
    }

    .presence-date {
      width: 100%;
    }

    .date-presence {
      display: flex !important;
      justify-content: flex-end !important;
      min-width: 0;
    }

    h1 {
      font-size: 1.35rem;
    }

    h6 {
      font-size: 0.78rem;
    }

    #chronometer2,
    #chronometer-dif {
      width: 1.9em;
    }

    #calendar-icon {
      width: 0.65em;
      height: 0.95em;
    }

    .text-calendar {
      font-size: 0.66rem;
    }
  }

  .total-presence-with-break {
    .card-notice1 {
      >.d-flex {
        grid-template-areas:
          "worked break"
          "estimated diff";
      }
    }

    .presence-break {
      grid-area: break;
    }

    .dif {
      padding-right: clamp(88px, 14vw, 118px);
    }

    .date-presence {
      grid-area: diff;
      align-self: center;
      justify-self: end;
      position: static;
    }
  }
}

@media (min-width: 1151px) and (max-width: 1320px) {
  .total-presence {
    .card-notice1 {
      >.d-flex {
        align-items: center;
        column-gap: clamp(6px, 1vw, 14px);
        display: grid !important;
        grid-template-areas: "worked estimated diff date";
        grid-template-columns: minmax(115px, 1fr) minmax(135px, 1fr) minmax(170px, 1.2fr) minmax(98px, auto);
      }
    }

    .presence-worked {
      grid-area: worked;
    }

    .presence-break {
      grid-area: break;
    }

    .presence-estimated {
      grid-area: estimated;
    }

    .presence-diff {
      flex: initial;
      grid-area: diff;
      justify-content: center;
    }

    .date-presence {
      flex: initial;
      grid-area: date;
      justify-self: end;
      min-width: 0;
    }

    &.total-presence-with-break {
      .card-notice1 {
        >.d-flex {
          grid-template-areas: "worked break estimated diff date";
          grid-template-columns: minmax(115px, 1fr) minmax(115px, 1fr) minmax(135px, 1fr) minmax(170px, 1.15fr) minmax(98px, auto);
        }
      }
    }

    h1 {
      font-size: clamp(1rem, 1.35vw, 1.45rem);
    }

    h6 {
      font-size: clamp(0.65rem, 0.78vw, 0.82rem);
    }

    #chronometer2,
    #chronometer-dif {
      width: clamp(1.55em, 1.8vw, 2em);
    }

    .text-calendar {
      font-size: clamp(0.64rem, 0.78vw, 0.78rem);
      max-width: 112px;
      text-align: right;
    }
  }
}

@media (min-width: 768px) {
  .total-presence-mobile {
    display: none;
  }

  .header-mobile {
    display: none;
  }

  .table-presence-mobile {
    display: none;
  }
}

@media (min-width: 768px) and (max-width: 1200px) {
  .header {
    .card-notice {
      .row {
        row-gap: 12px;
      }

      .col {
        flex: 1 1 230px;
        max-width: none;
      }

      h1 {
        font-size: clamp(1.5rem, 2.8vw, 2rem);
        margin-bottom: 0;
      }

      h6 {
        margin-bottom: 0;
      }

      :deep(.btn-action) {
        min-width: 11rem;
      }
    }
  }
}

@media (max-width: 767px) {
  #header-title {
    display: none;
  }

  .total-presence {
    display: none;
  }

  .header {
    display: none;
  }

  #table-presence {
    display: none;
  }

  .header-mobile {
    .buttons-presence-header>div {
      width: 100%;
      flex-direction: column;
      align-items: center;

      :deep(.btn-action) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        box-sizing: border-box;
        width: 17rem;
        max-width: calc(100% - 1rem);
        height: 3rem;
        font-size: 1rem;
      }
    }

    .card-notice {
      padding: 5px 10px;

      #chronometer {
        fill: #F7D20E;
        min-width: 30px;
      }

      .text-title-header {
        font-size: 28px !important;
        color: white;
      }

      .text-total-header {
        font-size: 20px;
        font-weight: 800;
      }

      .totalTime-header {
        font-size: 18px;
      }

      .row>.el-row {
        padding-right: 0;
      }

      .presence-header-pause-row {
        background-color: #fb8e00;
        border-radius: 0.6em;
        margin: 4px 0;
        padding: 6px 4px;
      }

      .row>.el-row>.col.justify-content-end {
        column-gap: 3px;
        padding-right: 0;
      }

      .row>.el-row>.col.justify-content-end .text-presence-today.mx-2 {
        margin-left: 2px !important;
        margin-right: 2px !important;
      }

      .row>.el-row>.col.justify-content-end .signPresenceHeader {
        margin-right: 0;
      }

      .signPresenceHeader {
        margin-top: -10px;

        .iconNav {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .table-presence-mobile {
    margin-top: 10px;

    .date-item-presence {
      color: $bg-blue;
      padding: 10px;

      #calendar-icon-table-mobile {
        margin-top: -3px;
        fill: $bg-blue;
        width: 20px;
        height: 20px;
      }
    }

    .card-presence-item {
      padding: 10px 0px;
      border-radius: 16px;
      font-size: 18px;

      .presence-item-span {
        color: $bg-blue;
        font-weight: 800;
        font-size: 16px;
        line-height: 100%;
        letter-spacing: -5%;
      }

      .iconNav {
        color: $bg-blue;
      }

      .time-presence-item {
        font-size: 18px;
        margin-left: 5px;
      }

      .worked-item-presence {
        .worked-item-presence-icon {
          color: $bg-blue;
          font-size: 22px;
          margin-right: 4px;
        }

        .worked-item-presence-text {
          font-weight: 800;
          margin-right: 7px;
        }

        .worked-item-presence-total {
          font-weight: 400;
        }

        .worked-item-presence-total,
        .worked-item-presence-text {
          margin-top: 3px;
          font-size: 18px;
          color: $bg-blue;
        }
      }
    }
  }

}

@media (max-width: 610px) {
  .new-presence-date {
    width: 100%;
  }

  .new-presence-button {
    width: 99%;
    margin-top: 15px;
    margin-left: 0px !important;
  }

}

@media (max-width: 550px) {
  .btn-add-day {
    max-width: 100% !important;
  }
}

@media (max-width: 405px) {
  .header-mobile {
    .card-notice {
      .text-presence-today {
        font-size: 12px;
      }

      .signPresenceHeader {
        .iconNav {
          height: 18px;
          width: 18px;
        }
      }
    }
  }
}
</style>
